import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { selectRoles } from "../../../../features/roleSlice";
import { selectUser } from "../../../../features/userSlice";
import {
  getLeadTimes,
  saveLeadTimeBrand,
  deleteLeadTimeBrand,
  getLeadTimeRecipients,
  previewLeadTimeEmail,
  sendLeadTimeEmail,
} from "./api";
import { starterBrands } from "./starterData";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { PageHeader, PageShell } from "../../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../../components/UI/States";
import { Banner } from "../../../../components/WindowDoor/wdUi";

const makeId = () => Math.random().toString(36).slice(2, 10);
const brandKeyOf = (name) => name.trim().toLowerCase();

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatTimestamp = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const emptyItem = () => ({ id: makeId(), product: "", was: "", now: "", note: "" });

// Themed control surface (follows classic/glass × light/dark via tokens).
const inputClass =
  "app-input rounded-xl border px-3 py-2 text-sm transition disabled:cursor-not-allowed disabled:opacity-50";

const secondaryBtn = "app-btn app-btn-secondary app-btn-sm";
const iconDangerBtn =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-rose-600 transition hover:bg-rose-500/10 dark:text-rose-400";

// react-modal: positioning stays inline; the surface is the floating glass
// material (.app-glass) so it follows every theme without a theme read.
const modalStyles = {
  overlay: { backgroundColor: "var(--app-scrim)", zIndex: 1300 },
  content: {
    position: "absolute",
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    width: "min(760px, 94vw)",
    maxHeight: "92vh",
    overflow: "auto",
    outline: "none",
  },
};

function LeadTimesManager() {
  const role = useSelector(selectRoles);
  const user = useSelector(selectUser);

  const [brands, setBrands] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [dirty, setDirty] = useState({});
  const [newBrandName, setNewBrandName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [emailModal, setEmailModal] = useState(null);

  const isManager =
    role?.roles?.includes("Window Door Manager") ||
    role?.roles?.includes("Admin");

  useEffect(() => {
    getLeadTimes()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setBrands(list);
        setSelectedKey(list[0]?.brandKey || null);
      })
      .catch(() => setBrands([]));
  }, []);

  // Warn before leaving the page with unsaved edits.
  useEffect(() => {
    if (!Object.keys(dirty).length) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const flashNotice = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 5000);
  };

  const patchBrand = (brandKey, updater) => {
    setBrands((prev) =>
      prev.map((b) => (b.brandKey === brandKey ? updater(b) : b))
    );
    setDirty((prev) => ({ ...prev, [brandKey]: true }));
  };

  const patchSection = (brandKey, sectionId, updater) =>
    patchBrand(brandKey, (b) => ({
      ...b,
      sections: b.sections.map((s) => (s.id === sectionId ? updater(s) : s)),
    }));

  const patchItem = (brandKey, sectionId, itemId, field, value) =>
    patchSection(brandKey, sectionId, (s) => ({
      ...s,
      items: s.items.map((i) =>
        i.id === itemId ? { ...i, [field]: value } : i
      ),
    }));

  const handleAddBrand = () => {
    const name = newBrandName.trim();
    if (!name) return;
    const key = brandKeyOf(name);
    if (brands.some((b) => b.brandKey === key)) {
      setError(`${name} already exists.`);
      return;
    }
    const maxOrder = brands.reduce(
      (max, b) => Math.max(max, Number(b.sortOrder) || 0),
      0
    );
    const brand = {
      brand: name,
      brandKey: key,
      effectiveDate: todayStr(),
      sortOrder: maxOrder + 1,
      sections: [{ id: makeId(), title: "", items: [emptyItem()] }],
    };
    setBrands((prev) => [...prev, brand]);
    setDirty((prev) => ({ ...prev, [key]: true }));
    setSelectedKey(key);
    setNewBrandName("");
    setError("");
  };

  const handleLoadStarter = () => {
    const starter = starterBrands();
    setBrands(starter);
    setSelectedKey(starter[0].brandKey);
    setDirty(Object.fromEntries(starter.map((b) => [b.brandKey, true])));
  };

  const handleDeleteBrand = async (brand) => {
    if (
      !window.confirm(
        `Delete all ${brand.brand} lead times? Salespeople will no longer see this brand.`
      )
    )
      return;
    if (brand.updatedAt) {
      try {
        await deleteLeadTimeBrand(brand.brandKey);
      } catch (e) {
        // Brand may only exist locally; remove it from view either way.
      }
    }
    setBrands((prev) => {
      const next = prev.filter((b) => b.brandKey !== brand.brandKey);
      setSelectedKey(next[0]?.brandKey || null);
      return next;
    });
    setDirty((prev) => {
      const next = { ...prev };
      delete next[brand.brandKey];
      return next;
    });
  };

  const handleStartNewUpdate = (brand) => {
    patchBrand(brand.brandKey, (b) => ({
      ...b,
      effectiveDate: todayStr(),
      sections: b.sections.map((s) => ({
        ...s,
        items: s.items.map((i) => ({ ...i, was: i.now })),
      })),
    }));
    flashNotice(
      "Current lead times copied to the Was column — enter the new lead times, then save."
    );
  };

  const openEmailModal = async (doc) => {
    try {
      const [preview, recipients] = await Promise.all([
        previewLeadTimeEmail(doc.brandKey),
        getLeadTimeRecipients(),
      ]);
      if (!preview?.html) {
        setError(preview?.error || "Could not build the email preview.");
        return;
      }
      setEmailModal({
        brandKey: doc.brandKey,
        brand: doc.brand,
        subject: preview.subject || "",
        html: preview.html,
        to: (recipients?.to || []).join(", "),
        cc: (recipients?.cc || []).join(", "),
        saveAsDefault: false,
        sending: false,
        sent: false,
        error: "",
      });
    } catch (e) {
      setError("Could not build the email preview.");
    }
  };

  const handleSave = async (brand, openEmail = false) => {
    setSaving(true);
    setError("");
    try {
      const res = await saveLeadTimeBrand({
        ...brand,
        updatedBy: user?.name || "",
        updatedByEmail: user?.email || "",
      });
      if (res?.success && res.doc) {
        setBrands((prev) =>
          prev.map((b) =>
            b.brandKey === brand.brandKey ? { ...b, ...res.doc } : b
          )
        );
        setDirty((prev) => {
          const next = { ...prev };
          delete next[brand.brandKey];
          return next;
        });
        flashNotice(`${brand.brand} lead times saved.`);
        if (openEmail) await openEmailModal(res.doc);
      } else {
        setError(res?.error || "Save failed.");
      }
    } catch (e) {
      setError("Save failed — check your connection and try again.");
    }
    setSaving(false);
  };

  const handleSendEmail = async () => {
    setEmailModal((m) => ({ ...m, sending: true, error: "" }));
    try {
      const res = await sendLeadTimeEmail({
        brandKey: emailModal.brandKey,
        to: emailModal.to,
        cc: emailModal.cc,
        subject: emailModal.subject,
        saveAsDefault: emailModal.saveAsDefault,
        sentBy: user?.name || "",
      });
      if (res?.success) {
        setEmailModal((m) => ({ ...m, sending: false, sent: true }));
        setBrands((prev) =>
          prev.map((b) =>
            b.brandKey === emailModal.brandKey
              ? { ...b, lastEmailedAt: new Date().toISOString() }
              : b
          )
        );
      } else {
        setEmailModal((m) => ({
          ...m,
          sending: false,
          error: res?.error || "Send failed.",
        }));
      }
    } catch (e) {
      setEmailModal((m) => ({
        ...m,
        sending: false,
        error: "Send failed — check your connection and try again.",
      }));
    }
  };

  if (!role?.roles) {
    return (
      <PageShell size="sm">
        <div className="app-card rounded-2xl border">
          <LoadingState label="Checking access…" />
        </div>
      </PageShell>
    );
  }

  if (!isManager) {
    return (
      <PageShell size="sm">
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={LockOutlinedIcon}
            title="Lead Time Manager"
            description={
              <>
                This page is limited to the Window Door Manager. Current lead
                times are available to everyone on the{" "}
                <Link to="/windowdoor/lead-times" className="app-link underline">
                  Lead Times
                </Link>{" "}
                page.
              </>
            }
          />
        </div>
      </PageShell>
    );
  }

  const selectedBrand = brands?.find((b) => b.brandKey === selectedKey);

  return (
    <PageShell size="xl">
      <PageHeader
        icon={EditCalendarOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Lead Time Manager"
        description="Maintain window and door lead times by brand. Save keeps your changes; Save & Email also opens a formatted update email to send to the sales team."
        actions={
          <Link to="/windowdoor/lead-times" className="app-btn app-btn-secondary">
            <ScheduleOutlinedIcon fontSize="small" />
            View sales page
          </Link>
        }
      />

      <div className="space-y-5">
      {notice && <Banner tone="success">{notice}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}

      {brands === null ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading lead times…" />
        </div>
      ) : (
        <>
          <div className="app-card flex flex-wrap items-center gap-2 rounded-2xl border p-3">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Brands">
              {brands.map((b) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedKey === b.brandKey}
                  key={b.brandKey}
                  onClick={() => setSelectedKey(b.brandKey)}
                  className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                    selectedKey === b.brandKey
                      ? "bg-[color:var(--app-accent-solid)] text-white"
                      : "app-muted hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
                  }`}
                >
                  {b.brand}
                  {dirty[b.brandKey] ? (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-amber-400"
                      title="Unsaved changes"
                      aria-label="Unsaved changes"
                    />
                  ) : null}
                </button>
              ))}
            </div>
            <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
              <input
                aria-label="New brand name"
                className={`${inputClass} min-w-0 flex-1 sm:w-48`}
                placeholder="New brand name"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddBrand()}
              />
              <button
                type="button"
                onClick={handleAddBrand}
                className="app-btn app-btn-secondary"
              >
                Add Brand
              </button>
            </div>
          </div>

          {brands.length === 0 && (
            <div className="app-card rounded-2xl border">
              <EmptyState
                icon={ScheduleOutlinedIcon}
                title="No lead times yet"
                description="Start from the current Marvin and Andersen lists, or add a brand above."
                action={
                  <button
                    type="button"
                    onClick={handleLoadStarter}
                    className="app-btn app-btn-primary"
                  >
                    Load Marvin &amp; Andersen starter data
                  </button>
                }
              />
            </div>
          )}

          {selectedBrand && (
            <div className="app-card rounded-2xl border p-5">
              <div className="flex flex-wrap items-end justify-between gap-3 border-b app-divider pb-4">
                <div>
                  <h2 className="text-lg font-semibold">{selectedBrand.brand}</h2>
                  <p className="app-muted mt-1 text-xs">
                    {selectedBrand.updatedAt
                      ? `Last saved ${formatTimestamp(selectedBrand.updatedAt)}${
                          selectedBrand.updatedBy
                            ? ` by ${selectedBrand.updatedBy}`
                            : ""
                        }`
                      : "Not saved yet"}
                    {selectedBrand.lastEmailedAt
                      ? ` · Last emailed ${formatTimestamp(
                          selectedBrand.lastEmailedAt
                        )}`
                      : ""}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <span className="app-muted font-medium">Effective date</span>
                  <input
                    type="date"
                    className={inputClass}
                    value={selectedBrand.effectiveDate || ""}
                    onChange={(e) =>
                      patchBrand(selectedBrand.brandKey, (b) => ({
                        ...b,
                        effectiveDate: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2 py-4">
                <button
                  type="button"
                  onClick={() => handleStartNewUpdate(selectedBrand)}
                  className={secondaryBtn}
                  title="Copies every Now value into the Was column so you can enter the new lead times"
                >
                  Start New Update (copy Now → Was)
                </button>
                <button
                  type="button"
                  onClick={() =>
                    patchBrand(selectedBrand.brandKey, (b) => ({
                      ...b,
                      sections: [
                        ...b.sections,
                        { id: makeId(), title: "", items: [emptyItem()] },
                      ],
                    }))
                  }
                  className={secondaryBtn}
                >
                  Add Section
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteBrand(selectedBrand)}
                  className="app-btn app-btn-sm border-rose-500/30 text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
                >
                  Delete Brand
                </button>
                <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                  <button
                    type="button"
                    onClick={() => openEmailModal(selectedBrand)}
                    disabled={!selectedBrand.updatedAt || dirty[selectedBrand.brandKey]}
                    title={
                      dirty[selectedBrand.brandKey]
                        ? "Save first — the email is built from saved data"
                        : "Re-send the saved lead times"
                    }
                    className="app-btn app-btn-ghost"
                  >
                    Email…
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave(selectedBrand)}
                    disabled={saving}
                    className="app-btn app-btn-secondary"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave(selectedBrand, true)}
                    disabled={saving}
                    className="app-btn app-btn-primary"
                  >
                    {saving ? "Saving…" : "Save & Email…"}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {selectedBrand.sections.map((section) => (
                  <div
                    key={section.id}
                    className="app-surface rounded-xl border p-4"
                  >
                    <div className="flex items-center gap-2 pb-3">
                      <input
                        aria-label="Section title"
                        className={`${inputClass} min-w-0 flex-1 font-semibold`}
                        placeholder="Section title (e.g. Signature Ultimate, 400 Series) — optional"
                        value={section.title}
                        onChange={(e) =>
                          patchSection(
                            selectedBrand.brandKey,
                            section.id,
                            (s) => ({ ...s, title: e.target.value })
                          )
                        }
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            section.items.some(
                              (i) => i.product || i.was || i.now
                            ) &&
                            !window.confirm("Remove this section and its products?")
                          )
                            return;
                          patchBrand(selectedBrand.brandKey, (b) => ({
                            ...b,
                            sections: b.sections.filter(
                              (s) => s.id !== section.id
                            ),
                          }));
                        }}
                        className={iconDangerBtn}
                        title="Remove section"
                        aria-label="Remove section"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>

                    <div className="app-muted hidden items-center gap-2 px-1 pb-1.5 text-xs font-semibold sm:flex">
                      <span className="flex-1">Product</span>
                      <span className="w-28">Was</span>
                      <span className="w-28">Now</span>
                      <span className="flex-1">Note (optional)</span>
                      <span className="w-9" />
                    </div>

                    <div className="space-y-2">
                      {section.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex flex-wrap items-center gap-2 border-b app-divider pb-2 last:border-0 last:pb-0 sm:flex-nowrap sm:border-0 sm:pb-0"
                        >
                          <input
                            aria-label="Product"
                            className={`${inputClass} w-full flex-1 sm:w-auto`}
                            placeholder="Product"
                            value={item.product}
                            onChange={(e) =>
                              patchItem(
                                selectedBrand.brandKey,
                                section.id,
                                item.id,
                                "product",
                                e.target.value
                              )
                            }
                          />
                          <input
                            aria-label="Was"
                            className={`${inputClass} w-28`}
                            placeholder="Was"
                            value={item.was}
                            onChange={(e) =>
                              patchItem(
                                selectedBrand.brandKey,
                                section.id,
                                item.id,
                                "was",
                                e.target.value
                              )
                            }
                          />
                          <input
                            aria-label="Now"
                            className={`${inputClass} w-28`}
                            placeholder="Now"
                            value={item.now}
                            onChange={(e) =>
                              patchItem(
                                selectedBrand.brandKey,
                                section.id,
                                item.id,
                                "now",
                                e.target.value
                              )
                            }
                          />
                          <input
                            aria-label="Note"
                            className={`${inputClass} w-full flex-1 sm:w-auto`}
                            placeholder="Note (shows in red on the email)"
                            value={item.note}
                            onChange={(e) =>
                              patchItem(
                                selectedBrand.brandKey,
                                section.id,
                                item.id,
                                "note",
                                e.target.value
                              )
                            }
                          />
                          <button
                            type="button"
                            onClick={() =>
                              patchSection(
                                selectedBrand.brandKey,
                                section.id,
                                (s) => ({
                                  ...s,
                                  items: s.items.filter(
                                    (i) => i.id !== item.id
                                  ),
                                })
                              )
                            }
                            className={iconDangerBtn}
                            title="Remove product"
                            aria-label="Remove product"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        patchSection(selectedBrand.brandKey, section.id, (s) => ({
                          ...s,
                          items: [...s.items, emptyItem()],
                        }))
                      }
                      className="app-link mt-3 inline-flex min-h-[32px] items-center text-sm"
                    >
                      + Add product
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </div>

      <Modal
        isOpen={!!emailModal}
        onRequestClose={() =>
          !emailModal?.sending && setEmailModal(null)
        }
        style={modalStyles}
        className="app-glass app-glass-rim rounded-3xl border p-6"
        contentLabel="Send lead time email"
        ariaHideApp={false}
      >
        {emailModal && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">
                Email {emailModal.brand} lead times
              </h2>
              <p className="app-muted mt-1 text-sm">
                {emailModal.sent
                  ? "The update email has been sent."
                  : "Review the email below, adjust recipients, and send — or close to save without emailing."}
              </p>
            </div>

            {!emailModal.sent && (
              <div className="space-y-3">
                <label className="block text-sm">
                  <span className="font-semibold">Subject</span>
                  <input
                    className={`${inputClass} mt-1.5 w-full`}
                    value={emailModal.subject}
                    onChange={(e) =>
                      setEmailModal((m) => ({ ...m, subject: e.target.value }))
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold">To</span>
                  <input
                    className={`${inputClass} mt-1.5 w-full`}
                    placeholder="email@bigclumber.com, email2@bigclumber.com"
                    value={emailModal.to}
                    onChange={(e) =>
                      setEmailModal((m) => ({ ...m, to: e.target.value }))
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold">Cc</span>
                  <input
                    className={`${inputClass} mt-1.5 w-full`}
                    placeholder="Optional"
                    value={emailModal.cc}
                    onChange={(e) =>
                      setEmailModal((m) => ({ ...m, cc: e.target.value }))
                    }
                  />
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-sky-600"
                    checked={emailModal.saveAsDefault}
                    onChange={(e) =>
                      setEmailModal((m) => ({
                        ...m,
                        saveAsDefault: e.target.checked,
                      }))
                    }
                  />
                  Save these recipients as the default list
                </label>
              </div>
            )}

            <div>
              <div className="mb-1.5 text-sm font-semibold">Preview</div>
              <iframe
                title="Lead time email preview"
                srcDoc={emailModal.html}
                className="h-96 w-full rounded-xl border app-divider bg-white"
              />
            </div>

            {emailModal.error && (
              <Banner tone="error">{emailModal.error}</Banner>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setEmailModal(null)}
                disabled={emailModal.sending}
                className="app-btn app-btn-secondary"
              >
                {emailModal.sent ? "Done" : "Close without sending"}
              </button>
              {!emailModal.sent && (
                <button
                  type="button"
                  onClick={handleSendEmail}
                  disabled={emailModal.sending || !emailModal.to.trim()}
                  className="app-btn app-btn-primary"
                >
                  {emailModal.sending ? "Sending…" : "Send Email"}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}

export default LeadTimesManager;
